import dayjs from 'dayjs';
import queryString from 'query-string';
import 'react-quill/dist/quill.snow.css';
import { ArrowDown, RefreshCw } from 'react-feather';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleOutlined,
  CheckCircleTwoTone,
  CloseOutlined,
  EditOutlined,
  OpenAIOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Drawer,
  Modal,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import Api from '../../../api/index';
import { Images } from '../../../assets/images/Index';
import {
  BrandModel,
  LanguageModel,
  ProductFilter,
  ShopModel,
  VariantModel,
} from '../../../interfaces';
import { ProductImages } from './SectionsProducts/ProductImages';
import { VariantImages } from './SectionsProducts/VariantImages';
import { toastNotification } from '../../../utils/toastNotification';
import { ProductShopStatusEnum, ProductStatusEnum } from '../../../enums/ProductStatus.enum';
import { MappedAttribute } from './SectionsProducts/MappedAttributes';
import { ShopwareAttributes } from '../FilterAttributes/ShopwareAttributes';
import { ProductTranslateView } from './SectionsProducts/ProductTranslateView';
import { ProductTranslateModel } from '../../../interfaces/models/Product.model';
import { ShopProductCategories } from './SectionsProducts/ShopProductCategories';
import { LanguageEnum } from '../../../enums/Language.enum';
import { calculateTaxPrice } from '../../../utils';
import { ShopwareAttributesFilter } from '../FilterAttributes/ShopwareAttributesFilter';
import PriceField from './PriceField';
import { useAuthStore } from '../../../store/authenticated/authenticated.state';
import { PermissionEnum } from '../../../enums/Permission.enum';
import { countries } from '../LocalProductPage/countries';

interface ViewProductNewDesignProps {
  handleGetAllProducts: (lng: string) => void;
  brands: BrandModel[] | [];
  shopBrands: BrandModel[] | [];
  searchValue?: string;
  setSearchValue?: (value: ProductFilter) => void;
  languages?: LanguageModel[] | [];
  shop?: ShopModel;
  shopCategories: any[] | [];
  shopAttributes?: any;
  shopId: number;
}

interface MappedState {
  changes: {
    group_id: string;
    attributes: string[];
  };
  shop_attributes: string[];
  shop_categories: string[];
}

type FileImageState = {
  id: string;
  url?: string;
  is_cover: boolean;
  productId?: string;
  is_variant?: boolean;
  new_image?: boolean;
  is_deleted?: boolean;
};

export const ViewProductNewDesign = ({
  languages,
  shop,
  shopCategories,
  shopAttributes,
  shopBrands,
  handleGetAllProducts,
  shopId,
}: ViewProductNewDesignProps) => {
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [product, setProduct] = useState<any | null>(null);
  const [mappedAttributes, setMappedAttributes] = useState<{
    list: any;
    show: boolean;
  }>({
    list: [],
    show: false,
  });

  const { user } = useAuthStore();
  const checkAccess = (permission: string) => user?.permissions?.includes(permission);
  const isReadonly = !!checkAccess(PermissionEnum.READ_PRODUCT_RESELLER);

  // const [variantValidities, setVariantValidities] = useState<boolean[]>([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [fileList, setFileList] = useState<FileImageState[]>([]);
  const [selectedSellerAttributes, setSelectedSellerAttributes] = useState<string[]>([]);
  const [selectedVendorBrand, setSelectedVendorBrand] = useState(product?.brand?.name);
  const [isBrandChanged, setIsBrandChanged] = useState(false);
  const [mapedDelete, setMapedDelete] = useState<string[]>([]);
  const [translate, setTranslate] = useState<Partial<ProductTranslateModel> | null>(null);
  const [isCustomsCodeModalOpen, setIsCustomsCodeModalOpen] = useState(false);
  const [customsCodesByShop, setCustomsCodesByShop] = useState<Record<string, string>>({});
  const [shopIdsForModal, setShopIdsForModal] = useState<string[]>([]);
  const [mapped, setMapped] = useState<MappedState>({
    changes: {
      group_id: '',
      attributes: [],
    },
    shop_attributes: [],
    shop_categories: [],
  });

  const [attributesGroup, setAttributesGroup] = useState<
    {
      id: string;
      checkable?: boolean;
      title: string;
      children: {
        id: string;
        icon: any;
        checkable?: boolean;
        is_mapped?: boolean;
        is_deleted?: boolean;
        variant_id: string;
        title: string;
        attribute_group_id: string;
      }[];
    }[]
  >([]);

  // hooks
  const { search } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const parsed = queryString.parse(search);

  const id = useMemo(() => parsed?.id, [parsed?.id]);

  const disabledMapButton =
    mapped?.changes?.attributes?.length === 0 && mapped?.shop_attributes?.length === 0;

  // const categoriesForProduct = product?.product_categories.filter((category: any) =>
  //   product?.categories?.some(
  //     (productCategory: any) =>
  //       productCategory.category_id === category.id && !productCategory.is_deleted,
  //   ),
  // );

  const handleSetError = (msg: string) => {
    toastNotification('error', msg);
    setLoading(false);
    setLoadingButton(false);
  };

  const handleCopy = (event: any) => {
    const clipboardData = event.clipboardData || window.Clipboard;
    const selectedText = window?.getSelection()?.toString().trim();

    clipboardData.setData('text/plain', selectedText);
    event.preventDefault();
  };

  const handleSetImages = (files: FileImageState[]) => {
    setFileList(files);
    form.setFieldValue('files', files);
  };

  const handleClose = () => {
    setIsEditable(false);
    setVisible(false);
    delete parsed['id'];
    delete parsed['translate'];
    navigate({ search: queryString.stringify(parsed) }, { replace: true });
    setMappedAttributes({
      list: [],
      show: false,
    });
    form.resetFields();
  };

  // const handleBarcodeChangeVariants = (value: string, index: number) => {
  //   const isValidBarcodeValue = isValidBarcode(value);
  //   setVariantValidities(prevValidities => {
  //     const newValidities = [...prevValidities];
  //     newValidities[index] = isValidBarcodeValue;
  //     return newValidities;
  //   });
  // };

  const isValidBarcode = (barcode: string) => {
    // Check if barcode is undefined or null
    if (barcode === undefined || barcode === null) {
      return false;
    }

    // GTIN must be 8, 12, 13, or 14 digits long
    if (!/^\d{8,14}$/.test(barcode)) {
      return false;
    }

    // Reverse the digits for processing
    const reverseGtin = barcode.split('').reverse().join('');
    let sum = 0;

    // Apply the modulo 10 algorithm
    for (let i = 0; i < reverseGtin.length; i++) {
      const digit = parseInt(reverseGtin[i], 10);
      if (i % 2 === 0) {
        // For positions 1, 3, 5, etc. (odd positions in the GTIN, even indexes in zero-based array)
        sum += digit;
      } else {
        // For positions 2, 4, 6, etc. (even positions in the GTIN, odd indexes in zero-based array)
        sum += digit * 3;
      }
    }

    // Check if the sum is a multiple of 10
    return sum % 10 === 0;
  };

  const removeHtmlTags = (str: string) => str?.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, '');
  const setSelectedRows = (data: any) => setMapped({ ...mapped, ...data });
  const handleSetCategories = (shop_categories: any) => setSelectedRows({ shop_categories });
  const handleSetAttributes = (shop_attributes: any) => setSelectedRows({ shop_attributes });

  const handleSetSellerAttributes = (shop_attributes: any) => {
    setSelectedSellerAttributes(shop_attributes);
  };

  const handleSubmit = (formData: any) => {
    const data = {
      ...formData,
      ...(formData?.shop_warranty != null && {
        shop_warranty: parseInt(formData.shop_warranty),
      }),
      product_images: fileList
        ?.filter(file => !file.is_variant)
        ?.map(file => ({
          ...file,
          ...(file?.new_image ? { remote_id: file.id } : { id: file.id }),
          ...(file?.is_deleted ? { is_deleted: true } : { is_deleted: false }),
        })),
      product_translates: [
        translate,
        ...(product?.product_translates?.filter(
          (item: { id: string }) => item.id !== translate?.id,
        ) || []),
      ].filter(item => item?.name),
      append_categories: mapped?.shop_categories,
      deleted_group_mapped: mapedDelete,
      ...(!formData?.brand_id && {
        brand_id: isBrandChanged ? selectedVendorBrand : product.brand.id,
      }),
    };

    /* eslint-disable camelcase */
    Api(`product/${id}`, 'PUT', { data }, { shopId })
      .then(({ statusMessage, statusIsOk }) => {
        if (!statusIsOk) toastNotification('error', statusMessage);
        if (statusIsOk) {
          toastNotification('success', 'Suscessfully updated');
          handleGetAllProducts(LanguageEnum.EN);
          handleClose();
          setLoading(false);
          setMapped({
            changes: { group_id: '', attributes: [] },
            shop_attributes: [],
            shop_categories: [],
          });
          setMapedDelete([]);
        }
      })
      .catch(({ statusMessage }) => toastNotification('error', statusMessage));
  };

  const openCustomsCodeModal = async () => {
    if (!product) return;
    const customsInfo = JSON.parse(product.customs_information || '{}') || {};

    let allShopIds: string[] = [];
    try {
      const resp = await Api(`shops`, 'GET', null);
      if (resp?.statusIsOk && Array.isArray(resp?.result)) {
        allShopIds = resp.result
          .map((s: any) => String(s?.id))
          .filter((id: string) => !!id && !isNaN(Number(id)))
          .sort((a: string, b: string) => Number(a) - Number(b));
      }
    } catch (_e) {
      allShopIds = [];
      toastNotification('error', 'Failed to get shops');
    }

    const initialCodes: Record<string, string> = {};
    allShopIds.forEach(sid => {
      initialCodes[sid] = customsInfo?.[sid]?.customs_code ?? '';
    });

    setShopIdsForModal(allShopIds);
    setCustomsCodesByShop(initialCodes);
    setIsCustomsCodeModalOpen(true);
  };

  const applyCustomsCodesForShops = async () => {
    if (!product) return;
    // Validate: allow empty to keep unchanged; if not empty, must be numeric
    for (const sid of shopIdsForModal) {
      const val = (customsCodesByShop[sid] || '').trim();
      if (val && !/^[0-9]+$/.test(val)) {
        toastNotification('error', `Shop ${sid}: Customs code must contain only numbers`);
        return;
      }
    }

    const payloadCodes = Object.fromEntries(
      Object.entries(customsCodesByShop)
        .map(([sid, code]) => [sid, (code || '').trim()])
        .filter(([, code]) => code !== ''),
    );

    try {
      const resp = await Api(`product/customs-codes/bulk`, 'POST', { id, codes: payloadCodes });
      if (!resp?.statusIsOk) {
        toastNotification('error', resp?.statusMessage || 'Failed to update customs codes');
        return;
      }

      const updatedInfo = resp.result?.customs_information || resp?.customs_information;
      if (updatedInfo) {
        setProduct((prev: any) => ({
          ...prev,
          customs_information: JSON.stringify(updatedInfo),
        }));
      } else {
        // Fallback: update local state using submitted codes
        setProduct((prev: any) => {
          if (!prev) return prev;
          const prevInfo = JSON.parse(prev.customs_information || '{}') || {};
          const merged: any = { ...prevInfo };
          Object.entries(payloadCodes).forEach(([sid, code]) => {
            merged[sid] = { ...(merged[sid] || {}), customs_code: code };
          });
          return { ...prev, customs_information: JSON.stringify(merged) };
        });
      }

      // Reflect current shop code in visible form if provided
      const currentCode = payloadCodes[String(shopId)];
      if (currentCode) form.setFieldsValue({ customs_code: currentCode });

      setCustomsCodesByShop({});
      toastNotification('success', 'Customs codes updated for selected shops');
      setIsCustomsCodeModalOpen(false);
    } catch (_e) {
      toastNotification('error', 'Failed to update customs codes');
    }
  };

  const handleGetProductById = () => {
    setLoading(true);
    Api(`product/${id}`, 'GET', null, { shopId })
      .then(async ({ statusIsOk, statusMessage, result }) => {
        if (!statusIsOk) handleSetError(statusMessage);
        if (statusIsOk) {
          // Check CN code validity when loading product
          if (result.cncode && result.cncode.length === 8) {
            try {
              const cnResponse = await Api(
                `product/cn-code-description/${result.cncode}`,
                'GET',
                null,
                { shopId },
              );
              result.cn_code_invalid = !(
                cnResponse.statusIsOk && cnResponse.result?.description_en
              );
              result.cn_code_description = cnResponse.result?.description_en || null;
            } catch (error) {
              result.cn_code_invalid = true;
              result.cn_code_description = null;
            }
          }

          setProduct(result);
          const customsCode = JSON.parse(result.customs_information)?.[shopId]?.customs_code;
          form.setFieldsValue({ customs_code: customsCode });
          const customsDescription = JSON.parse(result.customs_information)?.[shopId]
            ?.customs_description;
          form.setFieldsValue({ customs_descriptions: customsDescription });
          form.setFieldsValue({ country_of_origin: result?.country_of_origin });
          setLoading(false);
          setMapedDelete([]);
          const variants =
            result?.variants?.map((variant: VariantModel) => ({
              ...variant,
              // in_shop: !!variant?.in_shop,
              price: calculateTaxPrice(variant?.price, variant?.tvsh),
              discount_price: calculateTaxPrice(variant?.discount_price, variant?.tvsh),
            })) || [];

          const coverImage = result.product_images.find(
            (image: FileImageState) => image.is_cover && !image.is_deleted,
          );
          const otherImages = result.product_images
            .filter((image: any) => !image.is_cover && !image.is_deleted)
            .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

          const productImages = coverImage ? [coverImage, ...otherImages] : otherImages;

          setFileList([...productImages]);

          form.setFieldsValue({
            ...result,
            ...(result?.brand?.mapped_brand && {
              mapped_brand_id: result?.brand?.mapped_brand?.shop_brand_id,
            }),
            ...(result?.brand && {
              brand_id: result?.brand?.id,
            }),
            variants,
          });
          setVisible(true);
        }
      })
      .catch(({ statusMessage }) => toastNotification('error', statusMessage));
  };

  const handleSetMapProductShopAttributes = () => {
    Api(
      `variant/create-attribute/${id}`,
      'POST',
      { attributes: selectedSellerAttributes },
      { shopId },
    )
      .then(({ statusIsOk, statusMessage }) => {
        if (!statusIsOk) handleSetError(statusMessage);
        if (statusIsOk) {
          toastNotification('success', statusMessage);
          handleGetAttGroupsByProductId();
        }
      })
      .catch(({ statusMessage }) => {
        handleSetError(statusMessage);
      });
  };

  const handleGetAttGroupsByProductId = () => {
    Api(`mapped-attributes/getAttributeGroupsByProductId/${id}`, 'GET', null, { shopId })
      .then(({ statusIsOk, statusMessage, result }) => {
        if (!statusIsOk) toastNotification('error', statusMessage);
        if (statusIsOk) {
          const filtered = result?.map((item: any) => ({
            id: item?.id,
            key: item?.id,
            selectable: false,
            checkable: false,
            title: item?.original_name,
            children: item?.attributes?.map((attribute: any) => ({
              checkable: false,
              id: attribute.id,
              key: attribute.id,
              variant_id: attribute.variant_id,
              is_filter: attribute.is_filter,
              is_deleted: attribute.is_deleted,
              title: attribute?.original_name,
              ...(attribute?.is_mapped == 1 && {
                icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
              }),
            })),
          }));

          setAttributesGroup(filtered);
        }
      })
      .catch(({ statusMessage }) => toastNotification('error', statusMessage));
  };

  const getMappedAttributes = (mapped_id: string) => {
    setLoading(true);
    Api(`mapped-attributes/getMappedAttributes/${mapped_id}`, 'GET', null, { shopId })
      .then(({ statusIsOk, statusMessage, result }) => {
        if (!statusIsOk) handleSetError(statusMessage);
        if (statusIsOk) {
          setMappedAttributes({
            list: result?.map((item: any, index: number) => ({
              ...item,
              key: item?.id.toString() + `${index}`,
            })),
            show: true,
          });
          setLoading(false);
        }
      })
      .catch(({ statusMessage }) => handleSetError(statusMessage));
  };

  const handlSelectAttribute = (selected: string) => {
    setSelectedAttribute(selected);
    getMappedAttributes(selected);
    setMapped({
      ...mapped,
      changes: { attributes: [selected], group_id: mapped?.changes?.group_id },
    });
  };

  const handleDeleteMapped = (id: string) => {
    setLoading(true);
    Api(`mapped-attributes/${id}`, 'DELETE', null, { shopId })
      .then(({ statusIsOk, statusMessage, attribute_id: group_id }) => {
        if (!statusIsOk) handleSetError(statusMessage);
        if (statusIsOk) {
          toastNotification('success', 'Successfully deleted mapping attributes');
          setSelectedAttribute('');
          handleGetAttGroupsByProductId();
          getMappedAttributes(group_id);
        }
      })
      .catch(({ statusMessage }) => handleSetError(statusMessage));
  };

  const handleDeleteAttribute = (selectedAttributeId: string) => {
    if (selectedAttributeId) {
      Api(
        `variant/delete-mapped-attribute`,
        'DELETE',
        {
          product_id: id,
          attribute_id: selectedAttributeId,
        },
        { shopId },
      )
        .then(({ statusIsOk, statusMessage }) => {
          if (!statusIsOk) handleSetError(statusMessage);
          if (statusIsOk) {
            toastNotification('success', statusMessage);
            handleGetAttGroupsByProductId();
          }
        })
        .catch(({ statusMessage }) => handleSetError(statusMessage));
    }
  };

  const handleMapFilterAttributes = () => {
    setLoadingButton(true);
    Api(
      `mapped-attributes`,
      'POST',
      {
        seller_id: product?.seller_id,
        selected_attributes_ids: mapped?.changes?.attributes,
        checkedKeys: mapped?.shop_attributes,
      },
      { shopId },
    )
      .then(({ statusIsOk, statusMessage }) => {
        if (!statusIsOk) handleSetError(statusMessage);
        if (statusIsOk) {
          toastNotification('success', 'Successfully mapping attributes');
          getMappedAttributes(selectedAttribute);
          handleGetAttGroupsByProductId();
          setMapped({
            ...mapped,
            shop_attributes: [],
          });
          setLoadingButton(false);
        }
      })
      .catch(({ statusMessage }) => handleSetError(statusMessage));
  };

  useEffect(() => {
    if (id) {
      handleGetProductById();
      handleGetAttGroupsByProductId();
      setSelectedVariant(null);
      setIsBrandChanged(false);
      setFileList([]);
      // setVariantValidities([]);
      setSelectedSellerAttributes([]);
      setAttributesGroup([]);
      setMapped({
        changes: { group_id: '', attributes: [] },
        shop_attributes: [],
        shop_categories: [],
      });
    }
  }, [id]);

  return (
    <Drawer
      open={visible}
      title="View"
      width={window.innerWidth - 260}
      onClose={handleClose}
      className="view_product new__view-product"
      footer={
        <div className="user-action-button">
          <Button onClick={handleClose} className="seller-cancel-button">
            {isEditable ? 'Cancel' : 'Close'}
          </Button>
          {!isReadonly && (
            <Button
              className="seller-save-button"
              type="primary"
              form="update-form"
              disabled={loading}
              htmlType="submit"
            >
              Update
            </Button>
          )}

          {!isReadonly && isEditable && (
            <Button
              disabled={loading}
              form="product-form"
              className="save-button"
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
          )}
        </div>
      }
    >
      {visible ? (
        <Form
          form={form}
          className="form__wrapper-variant"
          onFinish={handleSubmit}
          id="update-form"
        >
          <div className="product-view-wrapper new__product-view-wrapper">
            <div className="info__wrapper">
              <div className="assigne__wrapper">
                <div className="content">
                  <p>Assigned</p>
                  <div>
                    {product?.users_products?.[0]?.first_name
                      ? `${product?.users_products[0]?.first_name} ${product?.users_products[0]?.last_name}`
                      : '-'}
                  </div>
                </div>
                <div className="content">
                  <p>Status</p>
                  <div>
                    {Object.entries(ProductStatusEnum)
                      .filter(([k]) => isNaN(Number(k)))
                      .map(([key, value]) => {
                        if (value == product?.status) {
                          if (value === 4 && product?.productShop?.status !== undefined) {
                            if (product?.productShop?.status === ProductShopStatusEnum.PENDING) {
                              return <div key={key}>APPROVED</div>;
                            }
                            return (
                              <div key={key}>
                                {Object.entries(ProductShopStatusEnum)
                                  .filter(([k]) => isNaN(Number(k)))
                                  .map(
                                    ([shopKey, shopValue]) =>
                                      shopValue == product.productShop.status && (
                                        <div key={shopKey}>{shopKey.replace('_', ' ')}</div>
                                      ),
                                  )}
                              </div>
                            );
                          }
                          return <div key={key}>{key.replace('_', ' ')}</div>;
                        }
                        return null;
                      })}
                  </div>
                </div>
                <div className="last__updated">
                  Date added: {dayjs(product?.created_at).format('DD.MM.YYYY HH:mm') || '-'}
                </div>
                <div className="last__updated">
                  Last updated: {dayjs(product?.updated_at).format('DD.MM.YYYY HH:mm') || '-'}
                </div>
                <div className="last__updated">
                  Last Stock Change:{' '}
                  {dayjs(product?.stock_updated_at).format('DD.MM.YYYY HH:mm') || '-'}
                </div>
                <div className="last__updated">
                  In vendor list: {product?.is_in_vendor_list == 1 ? 'Yes' : 'No'}
                </div>
                <div className="last__updated">
                  Last Status Changed By: {product?.status_last_changed_by_name || '-'}
                </div>
              </div>
              <div className="variant__view-color">
                <div className="product-info">
                  <ProductImages
                    productId={product?.id}
                    is_variant={false}
                    images={fileList || []}
                    setImages={handleSetImages}
                  />
                  <div className="data__wrapper">
                    <p className="info-title">Product name</p>
                    <Form.Item name="name">
                      <Input type="text" readOnly={isReadonly} />
                    </Form.Item>
                  </div>

                  <div className="product__info">
                    <div className="data__wrapper">
                      <p className="info-title">SKU</p>
                      <p className="value" onCopy={handleCopy}>
                        {product?.sku || '-'}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">SKU intern</p>
                      <p className="value" onCopy={handleCopy}>
                        {product?.sku_intern || '-'}
                        {!product?.is_simple && (
                          <img
                            src={Images.variants}
                            alt="Variants"
                            style={{ marginLeft: '5px', width: '16px', height: '16px' }}
                          />
                        )}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">MPN</p>
                      <p className="value" onCopy={handleCopy}>
                        {product?.product_number || '-'}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">Seller</p>
                      <p className="value" onCopy={handleCopy}>
                        {product?.seller?.name || '-'}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">Sync</p>
                      <p className="value">
                        {product?.productShop ? (
                          <div
                            className={
                              product.productShop.status === 'in_shop' &&
                              product.is_updated === true
                                ? 'not-synced'
                                : product.productShop.status !== 'in_shop'
                                ? 'not-in-shop'
                                : 'in-shop'
                            }
                          >
                            <RefreshCw size={16} />
                            {product.productShop.status === 'in_shop' && product.is_updated === true
                              ? 'Not synced'
                              : product.productShop.status !== 'in_shop'
                              ? 'Not in shop'
                              : 'In shop'}
                          </div>
                        ) : (
                          <div className="not-in-shop">
                            <RefreshCw size={16} />
                            Not in shop
                          </div>
                        )}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">Express Delivery</p>
                      <p className="value" onCopy={handleCopy}>
                        {product?.express_delivery?.[shopId]?.express_delivery == 1 ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>

                  <div className="product__info" style={{ display: 'flex', gap: '24px' }}>
                    {/* CN Code Section */}
                    <div style={{ flex: 1 }}>
                      <div>
                        <div className="data__wrapper">
                          <Tooltip
                            title={
                              <div>
                                <p>
                                  <strong>Last Updated:</strong>{' '}
                                  {JSON.parse(product.customs_information)?.cncode?.updated_at ||
                                    'N/A'}
                                </p>
                                <p>
                                  <strong>Updated By:</strong>{' '}
                                  {JSON.parse(product.customs_information)?.cncode
                                    ?.last_updated_by || 'N/A'}
                                </p>
                              </div>
                            }
                          >
                            <p className="info-title">
                              CN Code{' '}
                              {JSON.parse(product.customs_information)?.cncode?.last_updated_by ===
                              'gpt' ? (
                                <OpenAIOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                              ) : (
                                <EditOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                              )}
                            </p>
                          </Tooltip>
                          <div className="value">
                            <Form.Item
                              name="cncode"
                              rules={[
                                { pattern: /^[0-9]+$/, message: 'Ju lutem vendosni vetëm numër' },
                              ]}
                            >
                              <Input
                                type="text"
                                readOnly={isReadonly}
                                onChange={async e => {
                                  const code = e.target.value.trim();
                                  if (code && code.length === 8) {
                                    try {
                                      const response = await Api(
                                        `product/cn-code-description/${code}`,
                                        'GET',
                                        null,
                                        { shopId },
                                      );
                                      if (response.statusIsOk && response.result?.description_en) {
                                        setProduct((prev: any) => ({
                                          ...prev,
                                          cn_code_description: response.result.description_en,
                                          cn_code_invalid: false,
                                        }));
                                      } else {
                                        setProduct((prev: any) => ({
                                          ...prev,
                                          cn_code_description: null,
                                          cn_code_invalid: true,
                                        }));
                                      }
                                    } catch (error) {
                                      setProduct((prev: any) => ({
                                        ...prev,
                                        cn_code_description: null,
                                        cn_code_invalid: true,
                                      }));
                                    }
                                  } else {
                                    // Clear description if code is not 8 chars
                                    setProduct((prev: any) => ({
                                      ...prev,
                                      cn_code_description: null,
                                      cn_code_invalid: false,
                                    }));
                                  }
                                }}
                              />
                            </Form.Item>
                            {product.cn_code_invalid && (
                              <div
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '14px',
                                  marginTop: '-12px',
                                }}
                              >
                                This is not a current valid CN code
                              </div>
                            )}
                            {product.cn_code_description && (
                              <div style={{ marginTop: '4px', color: '#666' }}>
                                <strong>CN Code Description:</strong> {product.cn_code_description}
                              </div>
                            )}
                          </div>
                          <div className="data__wrapper">
                            <div style={{ marginTop: '16px' }}>
                              <p className="info-title">Country of Origin</p>
                              <div className="value" style={{ marginTop: '10px' }}>
                                <h4
                                  style={{ marginBottom: '5px', color: '#000', fontSize: '12px' }}
                                >
                                  Vendor
                                </h4>
                                <Form.Item name="country" style={{ margin: 0 }}>
                                  <Select
                                    showSearch
                                    placeholder="Select country"
                                    style={{ width: '100%' }}
                                    disabled={isReadonly}
                                    optionFilterProp="children"
                                  >
                                    {countries.map((country, index) => (
                                      <Select.Option key={index} value={country}>
                                        {country}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <h4
                                  style={{
                                    marginBottom: '5px',
                                    marginTop: '10px',
                                    color: '#000',
                                    fontSize: '12px',
                                  }}
                                >
                                  GPT
                                </h4>
                                <Form.Item name="country_of_origin" style={{ margin: 0 }}>
                                  <Select
                                    showSearch
                                    placeholder="Select Customs country of origin"
                                    style={{ width: '100%' }}
                                    disabled={isReadonly}
                                    optionFilterProp="children"
                                  >
                                    {countries.map((country, index) => (
                                      <Select.Option key={index} value={country}>
                                        {country}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Customs Information Section */}
                    <div style={{ flex: 1 }}>
                      <div className="data__wrapper">
                        <Tooltip
                          title={
                            <div>
                              <p>
                                <strong>Last Updated By:</strong>{' '}
                                {JSON.parse(product.customs_information)?.[shopId]
                                  ?.last_updated_by || 'N/A'}
                              </p>
                            </div>
                          }
                        >
                          <p className="info-title">
                            Customs Information{' '}
                            {JSON.parse(product.customs_information)?.[shopId]?.last_updated_by ===
                            'gpt' ? (
                              <OpenAIOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                            ) : (
                              <EditOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                            )}
                          </p>
                        </Tooltip>
                        <div
                          className="value"
                          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <strong>Customs Import Code:</strong>
                            <Form.Item
                              name="customs_code"
                              style={{ margin: 0, width: '100%' }}
                              rules={[
                                { pattern: /^[0-9]+$/, message: 'Ju lutem vendosni vetëm numër' },
                              ]}
                            >
                              <Input type="text" readOnly={isReadonly} style={{ width: '100%' }} />
                            </Form.Item>
                            <Tooltip title="Change customs code for all shops">
                              <Button
                                size="middle"
                                onClick={openCustomsCodeModal}
                                disabled={isReadonly}
                              >
                                <ArrowDown size={18} />
                              </Button>
                            </Tooltip>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong>Customs Product Description:</strong>
                            <Form.Item name="customs_descriptions" style={{ margin: 0 }}>
                              <Input.TextArea
                                readOnly={isReadonly}
                                rows={2}
                                placeholder="Enter customs description"
                              />
                            </Form.Item>
                            {JSON.parse(product.customs_information || '{}')
                              .generic_customs_description && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                <strong>Generic Description:</strong>{' '}
                                {
                                  JSON.parse(product.customs_information)
                                    .generic_customs_description
                                }
                              </div>
                            )}
                          </div>
                          <div>
                            <strong>Import License Required:</strong>{' '}
                            {(() => {
                              const license = JSON.parse(product.customs_information)?.[shopId]
                                ?.license_required;

                              if (!license || license === null || license === undefined)
                                return 'Unknown';

                              if (typeof license === 'string') {
                                const trimmed = license.trim();
                                if (trimmed === '') return 'Unknown';
                                if (trimmed.toLowerCase() === 'no') return 'No';
                                return trimmed;
                              }

                              if (typeof license === 'boolean') {
                                return license ? 'Yes' : 'No';
                              }

                              return String(license);
                            })()}
                          </div>
                          <div>
                            <strong>Import VAT:</strong>{' '}
                            {product.variants?.[0]?.price_extended?.[shopId]?.tvsh + '%' || ''}
                          </div>
                          <div>
                            <strong>Customs TAX:</strong>{' '}
                            {product.variants?.[0]?.price_extended?.[shopId]?.customs_fee?.value +
                              '%' || ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Modal
                      title="Apply customs import code to all shops"
                      open={isCustomsCodeModalOpen}
                      onOk={applyCustomsCodesForShops}
                      onCancel={() => setIsCustomsCodeModalOpen(false)}
                      okText="Save"
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {shopIdsForModal.map(sid => (
                          <div key={sid} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <strong style={{ minWidth: 220 }}>
                              {`Customs import code - shop ${sid}`}
                            </strong>
                            <Input
                              placeholder="Leave blank to keep unchanged"
                              value={customsCodesByShop[sid] ?? ''}
                              onChange={e =>
                                setCustomsCodesByShop(prev => ({
                                  ...prev,
                                  [sid]: e.target.value,
                                }))
                              }
                              maxLength={30}
                            />
                          </div>
                        ))}
                        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                          Leave blank to keep current code. Only digits are allowed.
                        </div>
                      </div>
                    </Modal>
                  </div>

                  <div className="description__wrapper">
                    <div className="data__wrapper">
                      <p className="info-title">Categories</p>
                      <p className="value" style={{ display: 'flex', flexDirection: 'column' }}>
                        {Array.isArray(product?.breadcrumbs) ? (
                          product?.breadcrumbs?.map((b: string, i: number) => (
                            <span key={b + i}>- {b}</span>
                          ))
                        ) : (
                          <span>- {product?.breadcrumbs || 'No categories'}</span>
                        )}
                      </p>
                    </div>
                    <div className="data__wrapper">
                      <p className="info-title">Long description</p>
                      <p className="value">
                        {removeHtmlTags(product?.long_description?.trim()) || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="product__info">
                  <div className="data__wrapper">
                    <p className="info-title">Vendor Brand</p>
                    <Select
                      disabled={isReadonly}
                      size="large"
                      defaultValue={product?.brand?.name || '-'}
                      value={isBrandChanged ? selectedVendorBrand : product?.brand?.name}
                      showSearch={true}
                      optionFilterProp="children"
                      onChange={value => {
                        setSelectedVendorBrand(value);
                        setIsBrandChanged(true);
                        form.setFieldsValue({ mapped_brand_id: value });
                      }}
                    >
                      {shopBrands?.map((item: BrandModel, index: number) => (
                        <Select.Option
                          key={item.id?.toString() + `${index}`}
                          value={item.id}
                          label={item.name}
                        >
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  {/* <div className="data__wrapper">
                    <p className="info-title">Category</p>
                    <p className="value">
                      {categoriesForProduct?.map((item: any) => item.name).join(', ') || '-'}
                    </p>
                  </div> */}
                  <div className="data__wrapper">
                    <p className="info-title">Seller Warranty</p>
                    <p className="value">{product?.vendor_warranty || '-'}</p>
                  </div>
                  <div className="data__wrapper">
                    <p className="info-title">Foleja Warranty</p>
                    <Form.Item name="shop_warranty">
                      <Input type="number" min={1} readOnly={isReadonly} />
                    </Form.Item>
                  </div>
                </div>

                <Card bordered={false} style={{ marginTop: '20px' }} className="card-variants">
                  <div className="data__wrapper data__wrapper-variants">
                    <p className="info-title">Variants</p>
                  </div>
                  <div>
                    <Form.List name="variants">
                      {fields => (
                        <>
                          {fields.map(({ key, name, ...restField }) => {
                            const variantsEAN = form.getFieldValue(['variants', name])?.ean;
                            const variant = product.variants?.find(
                              (_v: any, idx: number) => idx === name,
                            );

                            const shopExtended = JSON.parse(variant?.shop_extended || '{}')?.[
                              shopId.toString()
                            ];

                            return (
                              <Space
                                key={key}
                                style={{
                                  display: 'flex',
                                  marginBottom: 8,
                                  cursor: 'pointer',
                                  ...(selectedVariant === product.variants[name]?.id
                                    ? { backgroundColor: '#d9d9d9' }
                                    : {}),
                                }}
                                align="end"
                                size={4}
                                onClick={() =>
                                  setSelectedVariant(
                                    selectedVariant === product.variants[name]?.id
                                      ? null
                                      : product.variants[name]?.id,
                                  )
                                }
                              >
                                <Form.Item
                                  {...restField}
                                  {...(key == 0 && {
                                    label: 'SKU',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  className="read__variant"
                                  name={[name, 'sku']}
                                  normalize={value => value.replace(/\D/g, '').trim()}
                                >
                                  <Input
                                    readOnly
                                    placeholder="SKU"
                                    autoComplete="off"
                                    autoCorrect="off"
                                  />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  {...(key == 0 && {
                                    label: 'Sku Intern',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  name={[name, 'sku_intern']}
                                  className="read__variant"
                                >
                                  <Input
                                    placeholder="SKU Intern"
                                    readOnly
                                    autoComplete="off"
                                    autoCorrect="off"
                                  />
                                </Form.Item>
                                <>
                                  <div style={{ display: 'flex' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <Tooltip
                                        placement="rightTop"
                                        title={
                                          isValidBarcode(variantsEAN)
                                            ? 'Valid Barcode'
                                            : 'Invalid Barcode'
                                        }
                                        className="more-info"
                                      >
                                        <div
                                          style={{
                                            width: '9px',
                                            height: '9px',
                                            borderRadius: '50%',
                                            backgroundColor: isValidBarcode(variantsEAN)
                                              ? 'green'
                                              : 'red',
                                            marginRight: '8px',
                                            marginTop: key === 0 ? '30px' : '-9px',
                                          }}
                                        ></div>
                                      </Tooltip>
                                    </div>
                                    <Form.Item
                                      {...restField}
                                      {...(key == 0 && {
                                        label: 'Ean',
                                      })}
                                      labelCol={{ span: 24 }}
                                      wrapperCol={{ span: 24 }}
                                      className="edit__variant-ean"
                                      name={[name, 'ean']}
                                      rules={[
                                        { required: false, message: 'Ju lutem vendosni SKU' },
                                      ]}
                                      normalize={value => value.trim()}
                                    >
                                      <Input
                                        // onChange={e =>
                                        //   handleBarcodeChangeVariants(e.target.value, name)
                                        // }
                                        readOnly={isReadonly}
                                        placeholder="EAN"
                                        // readOnly={disableField}
                                        // className={disableField ? 'readOnly' : 'input-variants'}
                                        // className="input-variants"
                                        autoComplete="off"
                                        autoCorrect="off"
                                      />
                                    </Form.Item>
                                  </div>
                                </>

                                <Form.Item
                                  {...restField}
                                  {...(key == 0 && {
                                    label: 'W(cm)',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  name={[name, 'width']}
                                  className="edit__variant-cm"
                                  normalize={value => value.replace(/[^\d.-]/g, '').trim()}
                                >
                                  <Input
                                    placeholder="Width"
                                    // readOnly={disableField}
                                    // className={disableField ? 'readOnly' : 'input-variants'}
                                    // className="input-variants"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    readOnly={isReadonly}
                                  />
                                </Form.Item>

                                <Form.Item
                                  {...restField}
                                  name={[name, 'height']}
                                  {...(key == 0 && {
                                    label: 'H(cm)',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  normalize={value => value.replace(/[^\d.-]/g, '').trim()}
                                  className="edit__variant-cm"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="Height"
                                    readOnly={isReadonly}
                                  />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'depth']}
                                  {...(key == 0 && {
                                    label: 'D(cm)',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  normalize={value => value.replace(/[^\d.-]/g, '').trim()}
                                  className="edit__variant-cm"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="Depth"
                                    readOnly={isReadonly}
                                  />
                                </Form.Item>

                                <Form.Item
                                  {...restField}
                                  {...(key == 0 && {
                                    label: 'Wt(kg)',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  name={[name, 'weight']}
                                  className="edit__variant-cm"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="Weight"
                                    readOnly={isReadonly}
                                  />
                                </Form.Item>

                                <PriceField
                                  index={name}
                                  form={form}
                                  restField={restField}
                                  id={key}
                                  shopId={shopId}
                                />

                                <Form.Item
                                  {...restField}
                                  name={[name, 'stock']}
                                  {...(key == 0 && {
                                    label: 'Stock',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  normalize={value => value.replace(/[^\d.-]/g, '').trim()}
                                  className="read__variant-last"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="Stock"
                                    readOnly
                                  />
                                </Form.Item>

                                {/* <Form.Item
                                  {...restField}
                                  name={[name, 'local_stock']}
                                  {...(key == 0 && {
                                    label: 'W.Stock',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  normalize={value => value.replace(/[^\d.-]/g, '').trim()}
                                  className="edit__variant-cm"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="W.stock"
                                    readOnly={isReadonly}
                                  />
                                </Form.Item> */}

                                {(() => {
                                  const additionalInfo =
                                    typeof product?.additional_info === 'string'
                                      ? (() => {
                                          try {
                                            return JSON.parse(product.additional_info as any);
                                          } catch {
                                            return null;
                                          }
                                        })()
                                      : (product?.additional_info as any);

                                  return additionalInfo?.vendor?.product_set ? (
                                    <Form.Item
                                      {...restField}
                                      {...(key == 0 && {
                                        label: 'MOQ',
                                      })}
                                      labelCol={{ span: 24 }}
                                      wrapperCol={{ span: 24 }}
                                      className="read__variant-last"
                                    >
                                      {Number(additionalInfo?.vendor?.product_set)}
                                    </Form.Item>
                                  ) : null;
                                })()}

                                <Form.Item
                                  {...restField}
                                  {...(key == 0 && {
                                    label: 'Del.days',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  name={[name, 'max_handling_days']}
                                  className="read__variant-last"
                                >
                                  <Input
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="Del.days"
                                    readOnly
                                  />
                                </Form.Item>

                                <Form.Item
                                  {...(key == 0 && {
                                    label: 'Shop status',
                                  })}
                                  labelCol={{ span: 24 }}
                                  wrapperCol={{ span: 24 }}
                                  className="read__variant"
                                >
                                  {shopExtended?.in_shop ? 'In shop' : 'Not in shop'}
                                </Form.Item>
                              </Space>
                            );
                          })}
                        </>
                      )}
                    </Form.List>
                    {selectedVariant && (
                      <div>
                        <VariantImages
                          images={
                            (selectedVariant &&
                              product?.variants?.find((x: VariantModel) => x.id === selectedVariant)
                                ?.variant_images) ||
                            []
                          }
                          handleGetAllProducts={handleGetAllProducts}
                          handleGetProductById={handleGetProductById}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              <Card bordered={false} style={{ marginTop: '20px' }} className="card-mapped-attrib">
                <div style={{ marginBottom: '20px' }}>
                  <h2 className="additional__filter-attributes">Mapped Attributes</h2>
                  {attributesGroup.length != 0 ? (
                    <div className="filter__mappingattribute-title">
                      <p>Attribute Group</p>
                      <p>Attribute Value</p>
                    </div>
                  ) : (
                    'No attributes in this product'
                  )}
                </div>
                <ViewAttributes
                  attributes={attributesGroup}
                  selectedVariant={selectedVariant}
                  selectedAttribute={selectedAttribute}
                  mappedAttributes={mappedAttributes}
                  handlSelectAttribute={handlSelectAttribute}
                  handleDeleteAttribute={handleDeleteAttribute}
                />
              </Card>
            </div>
            {!isReadonly && (
              <div className="product-translated-info view-product-translate">
                <div className="second-form">
                  <ProductTranslateView
                    product={product}
                    languages={languages}
                    shop={shop}
                    translate={translate}
                    defaultLanguage={isReadonly}
                    setTranslate={setTranslate}
                  />

                  <div className="shopware_view_attributes shopware_view_attributes-new">
                    <Row gutter={16}>
                      <Col span={24} className="status_username brand-create">
                        <Form.Item
                          label="Shop Brand"
                          name="mapped_brand_id"
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          rules={[{ message: 'Ju lutem vendosni brendin' }]}
                        >
                          <Select
                            size="large"
                            showSearch={true}
                            optionFilterProp="children"
                            placeholder="Shembull: Apple"
                            suffixIcon={<img src={Images.arrowCDown} />}
                            getPopupContainer={trigger => trigger.parentNode}
                            disabled={isReadonly}
                          >
                            {shopBrands?.map((item: any, index: number) => (
                              <Select.Option
                                key={item.id?.toString() + `${index}`}
                                value={item.id}
                                label={item.name}
                              >
                                {item.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    {!isReadonly && (
                      <ShopProductCategories
                        enableFields={false}
                        product_id={id}
                        loading={false}
                        visible={visible}
                        categories={shopCategories}
                        handleSelect={handleSetCategories}
                      />
                    )}
                    <div className="attribute_product_view">
                      {mappedAttributes?.show && (
                        <MappedAttribute
                          loading={loading}
                          isReadonly={isReadonly}
                          mappedAttributes={mappedAttributes?.list}
                          handleDeleteMapped={handleDeleteMapped}
                        />
                      )}
                    </div>

                    {!isReadonly && (
                      <ShopwareAttributes
                        title="Shop attributes"
                        buttonName="Map"
                        attributes={shopAttributes}
                        loadingButton={loadingButton}
                        disabled={disabledMapButton}
                        setSelectedData={handleSetAttributes}
                        handleMapFilterAttributes={handleMapFilterAttributes}
                        hideMapSaveBtn={false}
                      />
                    )}
                  </div>
                </div>
                {!isReadonly && (
                  <div>
                    <ShopwareAttributesFilter
                      title="Additional filter attributes"
                      visible={!!id}
                      buttonName="Add"
                      loadingButton={loading}
                      selectedShopAttributes={selectedSellerAttributes}
                      setSelectedData={handleSetSellerAttributes}
                      hideMapSaveBtn={id ? false : true}
                      handleMapFilterAttributes={handleSetMapProductShopAttributes}
                      sellerId={product.seller_id}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </Form>
      ) : (
        <div> Loading...</div>
      )}
    </Drawer>
  );
};

type ViewAttributesProps = {
  attributes: {
    id: string;
    checkable?: boolean | undefined;
    title: string;
    children: {
      id: string;
      icon: any;
      checkable?: boolean | undefined;
      is_mapped?: boolean | undefined;
      is_deleted?: boolean | undefined;
      variant_id: string;
      title: string;
      attribute_group_id: string;
    }[];
  }[];
  selectedVariant: string | null;
  selectedAttribute: string;
  mappedAttributes: {
    list: { id: string }[];
    show: boolean;
  };
  handlSelectAttribute: (selected: string) => void;
  handleDeleteAttribute: (selectedAttributeId: string) => void;
};

const ViewAttributes: FC<ViewAttributesProps> = ({
  attributes,
  selectedVariant,
  selectedAttribute,
  // mappedAttributes,
  handlSelectAttribute,
  handleDeleteAttribute,
}) => {
  return (
    <div className="mapped__attributes">
      {attributes?.map((item: any, index: number) => {
        const isSelected = item.children?.some((i: any) => i.id === selectedAttribute);

        return (
          <div
            className="attributes__group-title "
            key={item.id.toString() + `${index}`}
            style={{
              display: 'flex',
              flexFlow: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              borderBottom: '1px solid #F0F2F5',
              padding: '5px',
            }}
          >
            <Popconfirm
              title={
                <div>
                  <p className="title">Are you sure you want to delete this attribute?</p>
                </div>
              }
              className={isSelected ? 'tooltip-btn' : 'tooltip-btn-hidden'}
              okText="Yes"
              cancelText="No"
              {...(isSelected && {
                onConfirm: () => handleDeleteAttribute(selectedAttribute),
              })}
            >
              <Tooltip title={isSelected ? 'Delete selected attribute' : ''}>
                <Button
                  type="text"
                  icon={<CloseOutlined size={12} style={{ color: '#FF0000' }} />}
                />
              </Tooltip>
            </Popconfirm>

            <p
              style={{
                maxWidth: '200px',
                width: '100%',
                textTransform: 'capitalize',
                fontSize: '14px',
                color: '#9d9b9b',
                padding: 0,
                margin: 0,
                marginRight: '10px',
                marginLeft: '10px',
              }}
            >
              {item.title}
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexFlow: 'row',
                maxWidth: '660px',
                width: '100%',
                overflow: 'hidden',
                flexWrap: 'wrap',
              }}
            >
              {item?.children
                .filter((child: any) => child.is_deleted !== 1)
                .map(
                  (
                    atr: {
                      checkable: boolean;
                      id: string;
                      is_filter: number;
                      is_deleted: number;
                      key: string;
                      title: string;
                      variant_id: string;
                      icon: any;
                    },
                    idx: number,
                  ) => {
                    const isSelectedVariant = atr.variant_id == selectedVariant;

                    const isFilter = atr ? atr.is_filter : null;
                    const isMapped = atr.icon;

                    const successColor = isMapped ? 'success' : 'default';
                    const isSelctedAtr = selectedVariant
                      ? isSelectedVariant
                      : selectedAttribute === atr.id;

                    return (
                      <Tag
                        key={atr.id.toString() + '-' + atr?.variant_id + '-' + idx + 1}
                        icon={isMapped ? <CheckCircleOutlined /> : <></>}
                        style={{ cursor: 'pointer', padding: '2px 6px', fontSize: '14px' }}
                        color={isSelctedAtr ? '#9d9b9b' : successColor}
                        closable={false}
                        onClick={e => {
                          e.preventDefault();
                          handlSelectAttribute(atr.id);
                        }}
                      >
                        {atr.title}
                        {isFilter === 0 && (
                          <div style={{ height: '3px', background: 'grey', marginBottom: '2px' }} />
                        )}
                      </Tag>
                    );
                  },
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
